/**
 * Ask Steam what the signed-in member owns — and separately what they
 * wishlisted — for the BACKLOG's ALL / PLAYED / OWNED / WISHLISTED / UNOWNED
 * tabs, both trees.
 *
 * THE SAME SHAPE AS `useQueueArt`, and for the same reason recorded there: a
 * selector mints a fresh array every render, so an effect depending on the
 * array itself re-fires on every paint. The ref-against-a-joined-key is what
 * makes calling this from a render safe, and `ensureMyLibrary`'s in-flight set
 * is the second guard behind it — between them, a screen may call this as
 * freely as it calls the art queue.
 *
 * APPIDS, NOT IDS, which is the one place it differs from the art hook.
 * `steam_owned_apps` is keyed by appid and knows nothing about a locally minted
 * row, so the caller resolves them and drops the rows with no Steam id
 * (`backlogVM.bkAppids` does exactly that). A game left out this way stays
 * ABSENT from `myLib` rather than arriving as `null`, which is what keeps it
 * out of UNOWNED — see the field's doc-comment for why that distinction is the
 * whole contract.
 */

import { useEffect, useRef } from 'react';

import { useQd } from '../store/index.ts';

export function useEnsureMyLibrary(appids: readonly number[]): void {
  const ref = useRef<readonly number[]>(appids);
  ref.current = appids;
  const key = appids.join(',');
  useEffect(() => {
    if (ref.current.length) useQd.getState().ensureMyLibrary(ref.current);
  }, [key]);
}

/**
 * The WISHLISTED tab's read — one Steam call per session, on the first BACKLOG
 * render (`ensureWishlist` owns the once-per-session guard and the unlinked
 * gate; this only decides WHEN to offer).
 *
 * NO DEPENDENCY LIST BEYOND THE MOUNT, unlike the hook above, because the
 * question takes no arguments: the wishlist is the member's, not the shelf's.
 * `steamLink` is the one thing that could arrive late enough to matter — boot
 * fetches it in parallel — so this re-offers when the link lands, and
 * `ensureWishlist` throws away every offer after the first that finds it
 * linked.
 */
export function useEnsureWishlist(): void {
  const linked = useQd((s) => !!(s.steamLink && s.steamLink.linked));
  useEffect(() => {
    if (linked) useQd.getState().ensureWishlist();
  }, [linked]);
}
